<script setup lang="ts">
import type { FlightHydrated } from "@ca/db/flights";
import type { FlightRisk } from "@ca/risk/flight";

import {
	computed, ref, watch, 
} from "vue";
  
import { useMouse } from "@vueuse/core";
  
import { isCancelledFlight, riskToColor } from "@ca/risk/flight";

// tailwind purge hint
//elevated: "bg-red-500/20 text-red-500",
//low: "bg-green-500/20 text-green-500",
//moderate: "bg-yellow-500/20 text-yellow-700",
//unknown: "bg-gray-400/20 text-gray-400",


const props = defineProps<{
	flight: FlightHydrated;
	type?: "plan" | "actual" | "hybrid";
}>();

const { sourceType } = useMouse();
const wasEverTouch = ref(false);

watch(sourceType, (val) => {
	if (val === "touch") {
		wasEverTouch.value = true;
	}
});

const tipMode = computed(() => {
	if (wasEverTouch.value) {
		return "click";
	}
	return "hover";
});

  
const cancelled = computed(() => props.type === "actual" && isCancelledFlight(props.flight));

const makeFallbackRisk = (type: string) => {
	return {
		reasoning: cancelled.value ? "Flight was cancelled, so there is no actual risk" : "Risk could not be calculated",
		risk: null,
		type,
	};
};

const filteredRisks = computed(() => {
	if (cancelled.value) {
		return [];
	}
	const risks = props.flight.risks;
	if (props.type === "plan") {
		return risks.filter((r) => r.type.includes("plan"));
	}
	if (props.type === "actual") {
		return risks.filter((r) => !r.type.includes("plan"));
	}
	return risks;
});

const risks = computed(() => {
	let takeoffRisk: FlightRisk | null = null;
	let landingRisk: FlightRisk | null = null;
	let enRouteRisk: FlightRisk | null = null;
	filteredRisks.value.forEach((risk) => {
		if (risk.type.includes("takeoff")) {
			if (takeoffRisk === null || takeoffRisk.type.includes("plan")) {
				takeoffRisk = risk;
			}
		} else if (risk.type.includes("landing")) {
			if (landingRisk === null || landingRisk.type.includes("plan")) {
				landingRisk = risk;
			}
		} else if (risk.type.includes("route")) {
			if (enRouteRisk === null || enRouteRisk.type.includes("plan")) {
				enRouteRisk = risk;
			}
		}
	});
	const list = [takeoffRisk ?? makeFallbackRisk("takeoff"), enRouteRisk ?? makeFallbackRisk("route"), landingRisk ?? makeFallbackRisk("landing")];
	return list.map((r) => {
		const fixedType = r?.type.replace("plan-", "");
		return {
			...r,
			type: fixedType,
		};
	});
});
</script>
    
<template>
  <div
    class="grid grid-cols-3"
    :class="{ 'opacity-50': cancelled }"
  >
    <UPopover
      v-for="r in risks"
      :key="r.type"
      :text="r.reasoning ?? 'No reasoning provided'"
      :open-delay="0"
      :close-delay="50"
      :mode="tipMode"
      arrow
    >
      <div  
        class="flex flex-col items-center first:rounded-l-full last:rounded-r-full py-1"
        :class="riskToColor(r.risk).bgShade"
      >
        <div class="text-gray-500 text-sm">
          <UIcon
            class="size-6"
            :name="r.type === 'takeoff' ? 'mdi-plane-takeoff' : r.type === 'landing' ? 'mdi-plane-landing' : 'mdi-airplane-marker'"
          />
        </div>
        <div
          class="text-sm font-bold px-2 rounded-xs"
          :class="riskToColor(r.risk).text"
        >
          {{ r.risk ?? '--' }}
        </div>
      </div>
      <template #content>
        <div class="text-sm w-64 p-2">
          <h4 class="text-gray-500 text-sm font-cta mb-1">
            {{ r.type }} Risk
          </h4>
          {{ r.reasoning ?? 'No reasoning provided' }}
        </div>
      </template>
    </UPopover>
  </div>
</template>
    